/**
 * API Service - Connects React to Django REST API
 * 
 * Base URL: Uses REACT_APP_API_URL environment variable
 * Default: https://wdhd12345.pythonanywhere.com/api (production)
 * For local development, set REACT_APP_API_URL=http://127.0.0.1:8000/api in .env file
 * Auth: JWT (Bearer token)
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://wdhd12345.pythonanywhere.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      const currentPath = window.location.pathname;
      
      // Don't log 401 errors if already on login page (prevents console spam)
      if (currentPath !== '/login') {
        console.log('Authentication required. Redirecting to login...');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('username');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Campaigns API
export const campaignsAPI = {
  getAll: () => api.get('/campaigns/'),
  get: (id) => api.get(`/campaigns/${id}/`),
  create: (data) => api.post('/campaigns/', data),
  update: (id, data) => api.put(`/campaigns/${id}/`, data),
  delete: (id) => api.delete(`/campaigns/${id}/`),
  toggleActive: (id) => api.post(`/campaigns/${id}/toggle_active/`),
  // Lead management
  getLeads: (id) => api.get(`/campaigns/${id}/leads/`),
  assignLeads: (id, data) => api.post(`/campaigns/${id}/assign_leads/`, data),
  removeLeads: (id, data) => api.post(`/campaigns/${id}/remove_leads/`, data),
  getLeadAssignments: () => api.get('/campaigns/lead_assignments/'),
};

// Campaign Steps API
export const campaignStepsAPI = {
  getAll: (campaignId) => api.get('/campaign-steps/', { params: { campaign_id: campaignId } }),
  get: (id) => api.get(`/campaign-steps/${id}/`),
  create: (data) => api.post('/campaign-steps/', data),
  update: (id, data) => api.put(`/campaign-steps/${id}/`, data),
  delete: (id) => api.delete(`/campaign-steps/${id}/`),
};

// Templates API
export const templatesAPI = {
  getAll: () => api.get('/templates/'),
  get: (id) => api.get(`/templates/${id}/`),
  create: (data) => api.post('/templates/', data),
  update: (id, data) => api.put(`/templates/${id}/`, data),
  delete: (id) => api.delete(`/templates/${id}/`),
};

// Leads API
export const leadsAPI = {
  getAll: (status) => api.get('/leads/', { params: { status } }),
  get: (id) => api.get(`/leads/${id}/`),
  update: (id, data) => api.put(`/leads/${id}/`, data),
  pause: (id) => api.post(`/leads/${id}/pause/`),
  resume: (id) => api.post(`/leads/${id}/resume/`),
  importFromSheets: (options = {}) => api.post('/leads/import_from_sheets/', options),
};

// Outreach Logs API (Read-Only)
export const logsAPI = {
  getAll: (filters) => api.get('/outreach-logs/', { params: filters }),
  get: (id) => api.get(`/outreach-logs/${id}/`),
};

// Scheduler Runs API (Read-Only)
export const schedulerAPI = {
  getAll: () => api.get('/scheduler-runs/'),
  get: (id) => api.get(`/scheduler-runs/${id}/`),
};

// Analytics API (Read-Only)
export const analyticsAPI = {
  getOverview: (params) => api.get('/analytics/overview/', { params }),
  getCampaigns: (params) => api.get('/analytics/campaigns/', { params }),
  getChannels: (params) => api.get('/analytics/channels/', { params }),
  getTimeline: (params) => api.get('/analytics/timeline/', { params }),
  getInsights: (params) => api.get('/analytics/insights/', { params }),
  getCampaignROI: (campaignId) => api.get(`/analytics/${campaignId}/campaign_roi/`),
  getLeadJourney: (leadId) => api.get(`/analytics/lead_journey/${leadId}/`),
};

// Auth API (for JWT) - uses root URL, not /api/
// Extract base URL without /api suffix for auth endpoints
const getAuthBaseURL = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://wdhd12345.pythonanywhere.com/api';
  // Remove /api suffix if present
  return apiUrl.replace(/\/api\/?$/, '');
};

const authApi = axios.create({
  baseURL: getAuthBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add request interceptor for debugging
authApi.interceptors.request.use(
  (config) => {
    console.log('Auth API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('Auth API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
authApi.interceptors.response.use(
  (response) => {
    console.log('Auth API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('Auth API Response Error:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) => 
    authApi.post('/api/token/', { username, password }),
  refresh: (refresh) => 
    authApi.post('/api/token/refresh/', { refresh }),
};

export default api;

